import { FC, useState } from 'react'
import {
  convertAmountToPeriod,
  getItemAmountForPeriod,
  redistributeCurrencySplit,
} from '../../../lib/Calculator'
import { Spend, SpendAmountCreate } from '../../../lib/Types'
import { DistributionType } from '../lib/Enums/DistributionType'
import { SpendFrequency } from '../lib/Enums/SpendFrequency'
import { useBudget } from '../lib/Hooks/Budget'
import { useUpdateSpend } from '../lib/Hooks/Spends'
import SpendColumn from './SpendColumn'
import SpendUserColumns from './SpendUserColumns'
import TitleColumn from './TitleColumn'

interface Props {
  spend: Spend
  types?: DistributionType[]
  // Shows a combined month total column ahead of the per-user split, same
  // as expenses do - without it there's no way to see what a percentage
  // split is a percentage of.
  showTotalColumn?: boolean
  // Shows a yearly total column, same as expenses do.
  showYearColumn?: boolean
}

const IncomeRow: FC<Props> = ({
  spend,
  types = [DistributionType.CURRENCY],
  showTotalColumn = false,
  showYearColumn = false,
}) => {
  const { data: budget } = useBudget()
  const { mutate: updateSpend } = useUpdateSpend(budget!)
  const [isEditingTotal, setIsEditingTotal] = useState(false)
  const [isEditingYear, setIsEditingYear] = useState(false)

  const saveAmountForPeriod = (
    amount: number,
    period: SpendFrequency.MONTHLY | SpendFrequency.YEARLY,
  ) => {
    const latestAmount = spend.amounts.at(-1)!
    const newAmount = convertAmountToPeriod(
      { amount, frequency: period },
      latestAmount.frequency!,
    )

    if (newAmount !== latestAmount.amount) {
      const newAmounts = structuredClone(spend.amounts) as SpendAmountCreate[]
      newAmounts.push({
        formula: newAmount.toString(),
        frequency: latestAmount.frequency,
      })

      const {
        distribution_primary_user: distributionPrimaryUser,
        distribution_secondary_user: distributionSecondaryUser,
      } = redistributeCurrencySplit(spend, newAmount, !!budget!.user)

      updateSpend({
        ...spend,
        amounts: newAmounts,
        distribution_primary_user: distributionPrimaryUser,
        distribution_secondary_user: distributionSecondaryUser,
      })
    }
  }

  const saveTotal = (amount: number) => {
    saveAmountForPeriod(amount, SpendFrequency.MONTHLY)
    setIsEditingTotal(false)
  }

  const saveYear = (amount: number) => {
    saveAmountForPeriod(amount, SpendFrequency.YEARLY)
    setIsEditingYear(false)
  }

  return (
    <>
      <table
        className={`min-w-full table-budget ${budget!.user ? 'col-5' : ''}`}
      >
        <tbody>
          <tr className='bg-white border-b border-gray-300'>
            <td className='text-sm text-gray-900 font-light px-6 py-4 whitespace-nowrap'>
              <TitleColumn spend={spend} />
            </td>
            {showYearColumn && (
              <td className='text-sm text-gray-900 font-light px-6 py-4 whitespace-nowrap'>
                <SpendColumn
                  amount={getItemAmountForPeriod(spend, SpendFrequency.YEARLY)}
                  isActive={isEditingYear}
                  activate={() => setIsEditingYear(true)}
                  save={saveYear}
                />
              </td>
            )}
            {showTotalColumn && budget!.user && (
              <td className='text-sm text-gray-900 font-light px-6 py-4 whitespace-nowrap'>
                <SpendColumn
                  amount={getItemAmountForPeriod(spend, SpendFrequency.MONTHLY)}
                  isActive={isEditingTotal}
                  activate={() => setIsEditingTotal(true)}
                  save={saveTotal}
                />
              </td>
            )}
            <SpendUserColumns spend={spend} types={types} />
          </tr>
        </tbody>
      </table>
    </>
  )
}

export default IncomeRow
